import React, { useState } from 'react';
import './CreateJobAdvanced.scss';
import {
  Card,
  CardHeader,
  CardContent,
  TextField,
  Grid,
  FormControl,
  FormControlLabel,
  RadioGroup,
  Radio
} from '@mui/material';
import SecretField from '../SecretField';
import { createJobConfigHooks } from './hooks';
import { JobDetailsCard } from './components/JobDetailsCard';
import { SeedTalentScheduleCard } from './components/SeedTalentScheduleCard';
import ErrorBoundary from 'components/Error/ErrorBoundary';

const DEFAULT_SLACK_SERVICE = 'B0BDYV27ESH/FUYupw2qPgFFcC1yYV5sTEDL';

const DEFAULT_COURSE_COMPLETION_TYPE_ID = '154';

const DEFAULT_COURSE_IMPORT_TYPE_ID = '159';

const DEFAULT_CONFIG = {
  kronos: {
    type: '1password_item',
    '1password_item_path': 'Client credentials/UKG - EnjoyWurkAdmin - apiuser'
  },
  seedTalent: {
    baseUrl: 'https://api.seedtalent.com',
    platformApiKey: '',
    orgIntegrationKey: ''
  },
  report: {
    courseCompletion: DEFAULT_COURSE_COMPLETION_TYPE_ID,
    courseImport: DEFAULT_COURSE_IMPORT_TYPE_ID
  },
  companyName: '',
  customSlackService: DEFAULT_SLACK_SERVICE
};

const KronosAuthCard = ({ useJobConfig }) => {
  const [kronos, setKronos] = useJobConfig(['kronos']);
  const auth = kronos || {};
  const [mode, setMode] = useState(() =>
    auth.type === '1password_item' ? 'onepassword' : 'direct'
  );

  const switchMode = (newMode) => {
    setMode(newMode);
    if (newMode === 'onepassword') {
      setKronos({
        type: '1password_item',
        '1password_item_path': ''
      });
    } else {
      setKronos({
        companyName: auth.companyName || '',
        username: '',
        apiKey: '',
        password: '',
        baseUrl: 'https://secure.saashr.com'
      });
    }
  };

  const setAuthField = (field) => (value) =>
    setKronos({ ...auth, [field]: value });

  return (
    <Card>
      <CardHeader
        subheader="UKG credentials the job uses to read and write course records"
        title="Kronos"
      />
      <CardContent>
        <FormControl sx={{ marginBottom: '16px' }}>
          <RadioGroup
            onChange={(e, value) => switchMode(value)}
            row
            value={mode}
          >
            <FormControlLabel
              control={<Radio />}
              label="Reference a 1Password item"
              value="onepassword"
            />
            <FormControlLabel
              control={<Radio />}
              label="Enter credentials directly"
              value="direct"
            />
          </RadioGroup>
        </FormControl>

        {mode === 'onepassword' ? (
          <TextField
            fullWidth
            helperText="e.g. Client credentials/UKG - EnjoyWurkAdmin - apiuser"
            label="1Password item path"
            onChange={(e) =>
              setAuthField('1password_item_path')(e.target.value)
            }
            required
            value={auth['1password_item_path'] || ''}
            variant="standard"
          />
        ) : (
          <Grid container spacing={2}>
            <Grid item md={6} xs={12}>
              <TextField
                fullWidth
                helperText="UKG company code, e.g. EnjoyWurkAdmin"
                label="Company name"
                onChange={(e) => setAuthField('companyName')(e.target.value)}
                required
                value={auth.companyName || ''}
                variant="standard"
              />
            </Grid>
            <Grid item md={6} xs={12}>
              <TextField
                fullWidth
                label="Username"
                onChange={(e) => setAuthField('username')(e.target.value)}
                required
                value={auth.username || ''}
                variant="standard"
              />
            </Grid>
            <Grid item md={6} xs={12}>
              <SecretField
                label="API key"
                secret={auth.apiKey ?? ''}
                setState={setAuthField('apiKey')}
              />
            </Grid>
            <Grid item md={6} xs={12}>
              <SecretField
                label="Password"
                secret={auth.password ?? ''}
                setState={setAuthField('password')}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Base URL"
                onChange={(e) => setAuthField('baseUrl')(e.target.value)}
                required
                value={auth.baseUrl || ''}
                variant="standard"
              />
            </Grid>
          </Grid>
        )}
      </CardContent>
    </Card>
  );
};

const SeedTalentApiCard = ({ useJobConfig }) => {
  const [baseUrl, setBaseUrl] = useJobConfig(['seedTalent', 'baseUrl']);
  const [platformApiKey, setPlatformApiKey] = useJobConfig([
    'seedTalent',
    'platformApiKey'
  ]);
  const [orgIntegrationKey, setOrgIntegrationKey] = useJobConfig([
    'seedTalent',
    'orgIntegrationKey'
  ]);

  return (
    <Card>
      <CardHeader
        subheader="Credentials the client sets up in Seed Talent"
        title="Seed Talent API"
      />
      <CardContent>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Base URL"
              onChange={(e) => setBaseUrl(e.target.value)}
              required
              value={baseUrl || ''}
              variant="standard"
            />
          </Grid>
          <Grid item md={6} xs={12}>
            <SecretField
              label="Platform API key"
              secret={platformApiKey ?? ''}
              setState={setPlatformApiKey}
            />
          </Grid>
          <Grid item md={6} xs={12}>
            <SecretField
              label="Org integration key"
              secret={orgIntegrationKey ?? ''}
              setState={setOrgIntegrationKey}
            />
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
};

const ClientCard = ({ useJobConfig }) => {
  const [companyName, setCompanyName] = useJobConfig(['companyName']);
  const [companyId, setCompanyId] = useJobConfig(['companyId']);
  const [customSlackService, setCustomSlackService] = useJobConfig([
    'customSlackService'
  ]);

  return (
    <Card>
      <CardHeader
        subheader="Which client this course feed belongs to"
        title="Client"
      />
      <CardContent>
        <Grid container spacing={2}>
          <Grid item md={4} xs={12}>
            <TextField
              fullWidth
              helperText="Company short name in Wurk, e.g. MYHR"
              label="Company name"
              onChange={(e) => setCompanyName(e.target.value)}
              required
              value={companyName || ''}
              variant="standard"
            />
          </Grid>
          <Grid item md={4} xs={12}>
            <TextField
              fullWidth
              helperText="Numeric Wurk company ID"
              label="Company ID"
              onChange={(e) =>
                setCompanyId(
                  e.target.value === '' ? '' : Number(e.target.value)
                )
              }
              required
              type="number"
              value={companyId ?? ''}
              variant="standard"
            />
          </Grid>
          <Grid item md={4} xs={12}>
            <TextField
              fullWidth
              helperText="Slack webhook path for error notifications"
              label="Custom Slack service"
              onChange={(e) => setCustomSlackService(e.target.value)}
              value={customSlackService || ''}
              variant="standard"
            />
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
};

const ReportsCard = ({ useJobConfig }) => {
  const [courseCompletion, setCourseCompletion] = useJobConfig([
    'report',
    'courseCompletion'
  ]);
  const [courseImport, setCourseImport] = useJobConfig([
    'report',
    'courseImport'
  ]);

  return (
    <Card>
      <CardHeader
        subheader="Wurk report type IDs the job writes into"
        title="Reports"
      />
      <CardContent>
        <Grid container spacing={2}>
          <Grid item md={6} xs={12}>
            <TextField
              fullWidth
              helperText="Employee Certifications and Trainings template"
              label="Course completion type ID"
              onChange={(e) => setCourseCompletion(e.target.value)}
              required
              value={courseCompletion || ''}
              variant="standard"
            />
          </Grid>
          <Grid item md={6} xs={12}>
            <TextField
              fullWidth
              helperText="Import Courses template"
              label="Course import type ID"
              onChange={(e) => setCourseImport(e.target.value)}
              required
              value={courseImport || ''}
              variant="standard"
            />
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
};

export const SeedTalentCourseCompletionForm = () => {
  const { useJobGeneralConfig, useJobConfig } = createJobConfigHooks({
    partner: 'SEED TALENT',
    defaultConfig: DEFAULT_CONFIG
  });

  return (
    <ErrorBoundary context="Seed Talent course completion job form">
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <JobDetailsCard useJobGeneralConfig={useJobGeneralConfig} />
        </Grid>
        <Grid item xs={12}>
          <ClientCard useJobConfig={useJobConfig} />
        </Grid>
        <Grid item xs={12}>
          <KronosAuthCard useJobConfig={useJobConfig} />
        </Grid>
        <Grid item xs={12}>
          <SeedTalentApiCard useJobConfig={useJobConfig} />
        </Grid>
        <Grid item xs={12}>
          <ReportsCard useJobConfig={useJobConfig} />
        </Grid>
        <Grid item xs={12}>
          <SeedTalentScheduleCard useJobGeneralConfig={useJobGeneralConfig} />
        </Grid>
      </Grid>
    </ErrorBoundary>
  );
};

export default SeedTalentCourseCompletionForm;
